import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { type ComponentPropsWithoutRef, memo } from "react";
import type { ContentDirection } from "@/text-direction";

type MarkdownContentProps = {
  content: string;
  className?: string;
  direction?: ContentDirection;
};

type MarkdownCodeProps = ComponentPropsWithoutRef<"code"> & {
  inline?: boolean;
  node?: unknown;
};

const MarkdownContent = memo(({
  content,
  className,
  direction = "auto",
}: MarkdownContentProps) => {
  const containerClassName = className ? `space-y-4 ${className}` : "space-y-4";

  return (
    <div dir={direction} className={containerClassName}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...props }) => (
            <h1
              className="text-2xl font-bold text-[var(--text-primary)] mt-6 first:mt-0 mb-3"
              {...props}
            />
          ),
          h2: ({ node, ...props }) => (
            <h2
              className="text-xl font-semibold text-[var(--text-primary)] mt-6 first:mt-0 mb-3"
              {...props}
            />
          ),
          h3: ({ node, ...props }) => (
            <h3
              className="text-lg font-semibold text-[var(--text-primary)] mt-5 first:mt-0 mb-2"
              {...props}
            />
          ),
          p: ({ node, ...props }) => (
            <p
              className="text-lg text-[var(--text-primary)]/90 leading-relaxed whitespace-pre-wrap mb-3 last:mb-0"
              {...props}
            />
          ),
          ul: ({ node, ...props }) => (
            <ul
              className="list-disc ps-6 space-y-1 text-[var(--text-primary)]/90"
              {...props}
            />
          ),
          ol: ({ node, ...props }) => (
            <ol
              className="list-decimal ps-6 space-y-1 text-[var(--text-primary)]/90"
              {...props}
            />
          ),
          li: ({ node, ...props }) => (
            <li
              className="leading-relaxed marker:text-[var(--text-secondary)]"
              {...props}
            />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              className="border-s-2 border-[var(--accent)]/40 ps-4 italic text-[var(--text-secondary)]"
              {...props}
            />
          ),
          a: ({ node, ...props }) => (
            <a
              className="text-[var(--accent)] underline underline-offset-4"
              target="_blank"
              rel="noreferrer"
              {...props}
            />
          ),
          code: ({
            node,
            inline,
            className: codeClassName,
            ...props
          }: MarkdownCodeProps) => (
            <code
              className={
                inline
                  ? "rounded bg-[var(--glass-bg)]/80 px-1.5 py-0.5 font-mono text-sm text-[var(--text-primary)]/90"
                  : `font-mono text-sm text-[var(--text-primary)]/90 ${codeClassName || ""}`
              }
              {...props}
            />
          ),
          pre: ({ node, ...props }) => (
            <pre
              className="rounded-2xl bg-[var(--glass-bg)]/80 p-4 overflow-x-auto"
              {...props}
            />
          ),
          hr: ({ node, ...props }) => (
            <hr className="hairline my-4" {...props} />
          ),
          table: ({ node, ...props }) => (
            <table
              className="w-full text-start border-collapse text-sm"
              {...props}
            />
          ),
          th: ({ node, ...props }) => (
            <th
              className="border-b border-[var(--hairline)] pb-2 text-[var(--text-secondary)] font-semibold"
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td
              className="border-b border-[var(--hairline)] py-2 text-[var(--text-primary)]"
              {...props}
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});

export default MarkdownContent;
